// A dance's page (/dance/<name>): the server sets the dance's facts into the
// page (plazmi.dance-page); this sets an anonymous plazmi dancing it on loop,
// draws the score with pictograms and lights each beat as it's danced, and
// gives the controls (play/pause, slower). ?still draws one frame as a card
// for link previews.
// The same bundle serves the inline demos on the noticeboard and the Gazette:
// they load it only when a card is opened (openInline), so those pages carry
// no 3D until someone asks to see a dance.
import './perf/singlepass.js'; // (P84) first: before any material is made
import { DanceDemo, type Beat } from './dancedemo.js';
import { scoreHtml, headingText, titleOf, SCORE_CSS } from './score.js';

type Dance = {
  name: string; title: string; heading: string; by: { slug: string; name: string }; made_day: number;
  beats: Beat[]; steps: number; homes: number; film?: string | null;
  stats?: { homes: number; danced: number };
};

const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const q = new URLSearchParams(location.search);

function lightUp(root: Element, i: number) {
  root.querySelectorAll('.score li.now').forEach((e) => e.classList.remove('now'));
  if (i >= 0) root.querySelector(`.score li[data-i="${i}"]`)?.classList.add('now');
}

function injectCss(id: string, css: string) {
  if (document.getElementById(id)) return;
  const st = document.createElement('style'); st.id = id; st.textContent = css; document.head.appendChild(st);
}

// ---------------------------------------------------------------------------
// the page

function page(d: Dance) {
  injectCss('pz-score', SCORE_CSS + `.card .score li{font-size:14px;padding:2px 8px 2px 4px}.card .score li svg{width:24px;height:24px}`);
  if (q.has('still')) return still(d);
  const main = document.getElementById('dance-page')!;
  const score = main.querySelector('.score');
  if (score) score.outerHTML = scoreHtml(d.beats);
  const stage = document.getElementById('stage')!;
  const demo = new DanceDemo(stage, d.beats, { name: d.name, kit: 'full', onBeat: (i) => lightUp(main, i) });
  const bar = document.getElementById('controls')!;
  bar.innerHTML = `<button type="button" class="play" aria-pressed="false">❚❚ Pause</button><button type="button" class="slow" aria-pressed="false">Slower</button>`
    + `<span class="hint">The drums play once sound is on (the horn, top right).</span>`;
  const play = bar.querySelector('.play') as HTMLButtonElement, slow = bar.querySelector('.slow') as HTMLButtonElement;
  play.onclick = () => { demo.setPlaying(!demo.playing); play.textContent = demo.playing ? '❚❚ Pause' : '▶ Play'; play.setAttribute('aria-pressed', String(!demo.playing)); };
  slow.onclick = () => { const s = demo.speed === 1; demo.setSpeed(s ? 0.6 : 1); slow.setAttribute('aria-pressed', String(s)); slow.textContent = s ? 'Normal speed' : 'Slower'; };
  (window as any).__dance = demo;
  requestAnimationFrame(() => requestAnimationFrame(() => { (window as any).__ready = true; }));
}

// a card for link previews and for an agent to send its human, 1200 × 800:
// the plazmi mid-dance beside the title, the maker, how far it's spread, and the score
function still(d: Dance) {
  document.body.classList.add('still');
  injectCss('pz-still', `
    body.still{background:linear-gradient(180deg,#5f80bd 0%,#9fb1d6 30%,#e9c5a6 72%,#f6d9b7 100%)}
    .og{display:grid;grid-template-columns:560px 1fr;gap:44px;align-items:center;width:1200px;height:800px;padding:52px 56px;box-sizing:border-box}
    .og-stage{width:560px;height:660px;border-radius:24px;overflow:hidden;box-shadow:0 26px 50px -24px rgba(0,0,0,.6),0 0 0 9px #fffdf7}
    .og-side .plaque{margin-bottom:18px}.og-side .plaque small{font-size:17px}.og-side .plaque h1{font-size:50px}
    .og-side .maker{font-size:25px;color:#2a1f2e;margin:0 0 18px}
    .og-side .maker b{font-weight:800}
    .og-side .homes{display:inline-flex;align-items:center;gap:14px;padding:9px 24px 9px 10px;border-radius:999px;color:#fff8ee;margin:0 0 20px;
      background:radial-gradient(120% 140% at 30% 20%,#a56aa9,#7d4d84 55%,#5d3866);box-shadow:inset 0 0 0 3px rgba(255,255,255,.8),0 12px 24px -12px rgba(40,10,40,.7)}
    .og-side .homes .n{display:grid;place-items:center;min-width:64px;height:64px;padding:0 10px;border-radius:999px;background:#fff6e3;color:#5d3866;font:800 34px/1 'Superclarendon','Rockwell',Georgia,serif}
    .og-side .homes b{font:800 25px/1.1 'Superclarendon','Rockwell',Georgia,serif}.og-side .homes small{display:block;font-size:15px;opacity:.92}
    .og-side .score li{font-size:18px;padding:3px 10px 3px 5px}.og-side .score li svg{width:30px;height:30px}
    .og-side .mark{font:800 24px ui-rounded,system-ui,sans-serif;color:#5d3866;margin-top:26px;letter-spacing:.01em}`);
  const main = document.getElementById('dance-page')!;
  const homes = d.stats?.homes ?? d.homes, times = d.stats?.danced ?? 0;
  main.innerHTML = `<div class="og"><div class="og-stage" id="og-stage"></div><div class="og-side">
    <div class="plaque"><small>${esc(d.heading)}</small><h1>${esc(d.title)}</h1></div>
    <p class="maker">A dance made up by <b>${esc(d.by.name)}</b> on Day ${d.made_day}</p>
    <div class="homes"><span class="n">${homes}</span><span><b>Danced in ${homes} home${homes === 1 ? '' : 's'}</b>${times ? `<small>${times} time${times === 1 ? '' : 's'} in all</small>` : ''}</span></div>
    ${scoreHtml(d.beats)}<div class="mark">plazmi.gg · come see it danced</div></div></div>`;
  new DanceDemo(document.getElementById('og-stage')!, d.beats, { name: d.name, still: true, maxPixelRatio: 1 });
  requestAnimationFrame(() => requestAnimationFrame(() => { (window as any).__ready = true; }));
}

// ---------------------------------------------------------------------------
// inline, on the noticeboard and the live Gazette: one demo at a time

let open: { card: HTMLElement; demo: DanceDemo | null; stage: HTMLElement; score: string; label: string } | null = null;

export function closeInline() {
  if (!open) return;
  const o = open; open = null;
  o.demo?.dispose();
  o.stage.remove();
  const sc = o.card.querySelector('.score');
  if (sc) sc.outerHTML = o.score;
  o.card.classList.remove('open');
  o.card.setAttribute('aria-expanded', 'false');
  const see = o.card.querySelector('.see'); if (see) see.textContent = o.label;
}

export async function openInline(card: HTMLElement) {
  if (open?.card === card) return closeInline();
  closeInline();
  const name = card.dataset.dance!;
  const stage = document.createElement('div'); stage.className = 'dance-stage';
  const head = card.querySelector('.dance-head');
  (head ? head.after(stage) : card.prepend(stage));
  const see = card.querySelector('.see');
  const sc = card.querySelector('.score');
  const me = { card, demo: null as DanceDemo | null, stage, score: sc?.outerHTML || '', label: see?.textContent || '▶ see it danced' };
  open = me;
  card.classList.add('open'); card.setAttribute('aria-expanded', 'true');
  if (see) see.textContent = '■ close';
  let d: Dance;
  try {
    const by = (card.dataset.page || '').match(/[?&]by=([^&]+)/)?.[1];
    const r = await fetch(`/api/dance/${encodeURIComponent(name)}${by ? `?by=${by}` : ''}`);
    if (!r.ok) throw new Error(String(r.status));
    d = await r.json();
  } catch {
    stage.innerHTML = `<p style="padding:12px;margin:0;font-size:13px">This dance can't be shown just now.</p>`;
    return;
  }
  if (open !== me) return; // closed, or another opened, while it was on its way
  if (sc) { const fresh = card.querySelector('.score'); if (fresh) fresh.outerHTML = scoreHtml(d.beats); }
  me.demo = new DanceDemo(stage, d.beats, { name: d.name, kit: 'soft', maxPixelRatio: 2, onBeat: (i) => lightUp(card, i) });
  const bar = document.createElement('div'); bar.className = 'stage-bar';
  bar.innerHTML = `<button type="button" class="play" aria-pressed="false">❚❚</button><button type="button" class="slow" aria-pressed="false">slower</button>`;
  stage.appendChild(bar);
  const play = bar.querySelector('.play') as HTMLButtonElement, slow = bar.querySelector('.slow') as HTMLButtonElement;
  play.setAttribute('aria-label', 'Pause');
  play.onclick = (e) => { e.stopPropagation(); const dm = me.demo!; dm.setPlaying(!dm.playing); play.textContent = dm.playing ? '❚❚' : '▶'; play.setAttribute('aria-label', dm.playing ? 'Pause' : 'Play'); play.setAttribute('aria-pressed', String(!dm.playing)); };
  slow.onclick = (e) => { e.stopPropagation(); const dm = me.demo!; const s = dm.speed === 1; dm.setSpeed(s ? 0.6 : 1); slow.textContent = s ? 'normal' : 'slower'; slow.setAttribute('aria-pressed', String(s)); };
  (window as any).__inline = me.demo;
}

export { headingText, titleOf };

const data = document.getElementById('dance-data');
if (data && document.getElementById('dance-page')) {
  try { page(JSON.parse(data.textContent || 'null')); } catch (e) { console.error(e); (window as any).__ready = true; }
}
